import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiLink } from 'react-icons/fi';

const IntegrationSection = styled.section`
  padding: 6rem 2rem;
  background: rgba(10, 10, 15, 0.5);
  
  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const Badge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(100, 150, 255, 0.1);
  border: 1px solid rgba(100, 150, 255, 0.3);
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6496FF;
  margin-bottom: 1.5rem;
`;

const Title = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #FFFFFF 0%, #E8EAF0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.7);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const PlatformsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const PlatformCard = styled(motion.div)`
  background: rgba(25, 25, 35, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  backdrop-filter: blur(20px);
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    border-color: rgba(100, 150, 255, 0.3);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }
`;

const PlatformIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, rgba(100, 150, 255, 0.2) 0%, rgba(70, 120, 255, 0.2) 100%);
  border: 1px solid rgba(100, 150, 255, 0.3);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #6496FF;
  margin: 0 auto 1.5rem;
`;

const PlatformTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: #FFFFFF;
`;

const PlatformDescription = styled.p`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
`;

const CompatibilitySection = styled.div`
  text-align: center;
`;

const CompatibilityTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 2rem;
  color: #FFFFFF;
`;

const LogosContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3rem;
  flex-wrap: wrap;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    gap: 2rem;
  }
`;

const LogoCard = styled(motion.div)`
  background: rgba(25, 25, 35, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  min-width: 120px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    border-color: rgba(100, 150, 255, 0.3);
  }
`;

const LogoIcon = styled.div`
  font-size: 2rem;
  color: #6496FF;
`;

const LogoName = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
`;

const FeaturesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 2rem;
`;

const FeatureItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(100, 150, 255, 0.05);
  border: 1px solid rgba(100, 150, 255, 0.1);
  border-radius: 8px;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  
  &::before {
    content: '✓';
    color: #6496FF;
    font-weight: bold;
    font-size: 1rem;
  }
`;

const Integration = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const platforms = [
    {
      icon: "🧠",
      title: "Your AI Teammate",
      description: "Sits silently in every meeting, listening and understanding without ever interrupting the flow."
    },
    {
      icon: "⚡",
      title: "Instant Intelligence",
      description: "Get immediate answers to questions without context setup. Your copilot already knows what's happening."
    },
    {
      icon: "🎯",
      title: "High-Stakes Ready",
      description: "Perfect for consultants, sales engineers, and executives who need to maintain credibility."
    },
    {
      icon: "💻",
      title: "Universal Compatibility",
      description: "Works with Zoom, Teams, Google Meet, and any meeting platform on macOS and Windows."
    }
  ];

  const compatiblePlatforms = [
    { icon: "🍎", name: "macOS" },
    { icon: "🪟", name: "Windows" },
    { icon: "📹", name: "Zoom" },
    { icon: "👥", name: "Teams" },
    { icon: "🎥", name: "Google Meet" },
    { icon: "🌐", name: "Any Platform" }
  ];

  const features = [
    "No setup or integration required",
    "Works with any meeting platform",
    "Instant context understanding",
    "Real-time intelligent responses",
    "Maintains meeting confidentiality",
    "Zero disruption to workflow"
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <IntegrationSection id="integration" ref={ref}>
      <Container>
        <SectionHeader>
          <Badge
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <FiLink />
            Integration & Compatibility
          </Badge>
          
          <Title
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Works Everywhere You Do
          </Title>
          
          <Subtitle
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Ready for Zoom, Teams, Google Meet — or any meeting you join on macOS and Windows
          </Subtitle>
        </SectionHeader>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <PlatformsGrid>
            {platforms.map((platform, index) => (
              <PlatformCard
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
              >
                <PlatformIcon>
                  {platform.icon}
                </PlatformIcon>
                <PlatformTitle>{platform.title}</PlatformTitle>
                <PlatformDescription>{platform.description}</PlatformDescription>
              </PlatformCard>
            ))}
          </PlatformsGrid>
        </motion.div>

        <CompatibilitySection>
          <CompatibilityTitle>
            Available on All Major Platforms
          </CompatibilityTitle>
          
          <LogosContainer>
            {compatiblePlatforms.map((platform, index) => (
              <LogoCard
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <LogoIcon>{platform.icon}</LogoIcon>
                <LogoName>{platform.name}</LogoName>
              </LogoCard>
            ))}
          </LogosContainer>

          <FeaturesList>
            {features.map((feature, index) => (
              <FeatureItem
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                {feature}
              </FeatureItem>
            ))}
          </FeaturesList>
        </CompatibilitySection>
      </Container>
    </IntegrationSection>
  );
};

export default Integration; 